import { useMutation, useSuspenseQuery } from "@apollo/client";
import {
  Button,
  DataTable,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  PrimaryFullScreenSpinner,
  SelectItem,
  SelectSeparator,
  toast,
  useAppForm,
} from "@repo/ui";
import { getCountryLists, neverFunc } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import countries from "i18n-iso-countries";
import { MoreHorizontal } from "lucide-react";
import { Suspense, useEffect, useMemo, useState } from "react";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import { highRiskJurisdictionSourceValues } from "~/gql/client/enumValues";
import type {
  AdminHighRiskJurisdictionRowFragment,
  HighRiskJurisdictionSource,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/high-risk-jurisdictions")({
  head: () => opsHead("High-Risk Jurisdictions"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const adminHighRiskJurisdictionsDocument = graphql(`
  query AdminHighRiskJurisdictions {
    highRiskJurisdictions {
      ...AdminHighRiskJurisdictionRow
    }
  }

  fragment AdminHighRiskJurisdictionRow on HighRiskJurisdiction {
    id
    iso3CountryCode
    source
    reason
    createdAt
    updatedAt
  }
`);

const formSchema = z.object({
  iso3CountryCode: z
    .string({ message: "Please choose a jurisdiction" })
    .refine((value) => !!countries.getAlpha3Codes()[value], {
      message: "Please choose a jurisdiction",
    }),
  source: z.enum(highRiskJurisdictionSourceValues, {
    message: "Please choose a source",
  }),
  reason: z.string().nullable().optional(),
});

type FormSchema = z.input<typeof formSchema>;

function View() {
  const { data } = useSuspenseQuery(adminHighRiskJurisdictionsDocument);
  const [editingRow, setEditingRow] =
    useState<AdminHighRiskJurisdictionRowFragment | null>(null);
  const [deletingRow, setDeletingRow] =
    useState<AdminHighRiskJurisdictionRowFragment | null>(null);
  const [upsertDialogOpen, setUpsertDialogOpen] = useState(false);

  const [createHighRiskJurisdiction] = useMutation(
    graphql(`
      mutation CreateHighRiskJurisdiction($input: CreateHighRiskJurisdictionInput!) {
        createHighRiskJurisdiction(input: $input) {
          highRiskJurisdiction {
            ...AdminHighRiskJurisdictionRow
          }
        }
      }
    `),
  );

  const [updateHighRiskJurisdiction] = useMutation(
    graphql(`
      mutation UpdateHighRiskJurisdiction($input: UpdateHighRiskJurisdictionInput!) {
        updateHighRiskJurisdiction(input: $input) {
          highRiskJurisdiction {
            ...AdminHighRiskJurisdictionRow
          }
        }
      }
    `),
  );

  const [deleteHighRiskJurisdiction, { loading: deleteLoading }] = useMutation(
    graphql(`
      mutation DeleteHighRiskJurisdiction($input: DeleteHighRiskJurisdictionInput!) {
        deleteHighRiskJurisdiction(input: $input) {
          deletedId
        }
      }
    `),
  );

  const rows = (data.highRiskJurisdictions ?? []).slice().sort((a, b) => {
    const countryComparison = getCountryLabel(a.iso3CountryCode).localeCompare(
      getCountryLabel(b.iso3CountryCode),
    );

    if (countryComparison !== 0) {
      return countryComparison;
    }

    return a.source.localeCompare(b.source);
  });

  const columns = useMemo<
    ColumnDef<AdminHighRiskJurisdictionRowFragment & { id: string }>[]
  >(
    () => [
      {
        accessorKey: "jurisdiction",
        header: "Jurisdiction",
        cell: ({ row }) => getCountryLabel(row.original.iso3CountryCode),
      },
      {
        accessorKey: "iso3CountryCode",
        header: "ISO3",
      },
      {
        accessorKey: "source",
        header: "Source",
        cell: ({ row }) => getSourceLabel(row.original.source),
      },
      {
        accessorKey: "reason",
        header: "Reason",
        cell: ({ row }) => row.original.reason ?? "-",
      },
      {
        accessorKey: "updatedAt",
        header: "Updated at",
        cell: ({ row }) => row.original.updatedAt.toLocaleString(),
      },
      {
        id: "actions",
        header: () => <div className="sr-only">Actions</div>,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="secondary">
                <MoreHorizontal className="size-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => {
                  setEditingRow(row.original);
                  setUpsertDialogOpen(true);
                }}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setDeletingRow(row.original)}>
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [],
  );

  return (
    <AdminPageContainer testId="admin-high-risk-jurisdictions">
      <AdminPageHeader
        title="High-Risk Jurisdictions"
        subtitle="Manage jurisdictions that require elevated risk treatment."
        buttons={
          <Button variant="outline" onClick={() => setUpsertDialogOpen(true)}>
            Add jurisdiction
          </Button>
        }
      />
      <DataTable
        data={rows}
        columns={columns}
        className="overflow-hidden border"
      />

      <UpsertHighRiskJurisdictionDialog
        key={editingRow?.id ?? "new"}
        row={editingRow}
        open={upsertDialogOpen}
        onOpenChange={(open) => {
          setUpsertDialogOpen(open);
          if (!open && editingRow) {
            setEditingRow(null);
          }
        }}
        onSubmit={async (input) => {
          if (editingRow) {
            await updateHighRiskJurisdiction({
              variables: {
                input: {
                  id: editingRow.id,
                  ...input,
                },
              },
              refetchQueries: "active",
              awaitRefetchQueries: true,
              onCompleted() {
                toast({
                  kind: "success",
                  title: "High-risk jurisdiction updated",
                  autoHide: true,
                });
                setUpsertDialogOpen(false);
                setEditingRow(null);
              },
              onError() {
                toast({
                  kind: "error",
                  title: "Failed to update high-risk jurisdiction",
                  autoHide: true,
                });
              },
            });
            return;
          }

          await createHighRiskJurisdiction({
            variables: {
              input,
            },
            refetchQueries: "active",
            awaitRefetchQueries: true,
            onCompleted() {
              toast({
                kind: "success",
                title: "High-risk jurisdiction created",
                autoHide: true,
              });
              setUpsertDialogOpen(false);
            },
            onError() {
              toast({
                kind: "error",
                title: "Failed to create high-risk jurisdiction",
                autoHide: true,
              });
            },
          });
        }}
      />

      <Dialog
        open={deletingRow !== null}
        onOpenChange={(open) => {
          if (!open) {
            setDeletingRow(null);
          }
        }}
      >
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Delete high-risk jurisdiction</DialogTitle>
          </DialogHeader>
          <p>
            {deletingRow
              ? `Delete ${getCountryLabel(deletingRow.iso3CountryCode)} (${getSourceLabel(deletingRow.source)})?`
              : null}
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              loading={deleteLoading}
              disabled={deleteLoading || deletingRow === null}
              onClick={async () => {
                if (!deletingRow) {
                  return;
                }

                await deleteHighRiskJurisdiction({
                  variables: {
                    input: {
                      id: deletingRow.id,
                    },
                  },
                  refetchQueries: "active",
                  awaitRefetchQueries: true,
                  onCompleted() {
                    toast({
                      kind: "success",
                      title: "High-risk jurisdiction deleted",
                      autoHide: true,
                    });
                    setDeletingRow(null);
                  },
                  onError() {
                    toast({
                      kind: "error",
                      title: "Failed to delete high-risk jurisdiction",
                      autoHide: true,
                    });
                  },
                });
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageContainer>
  );
}

function UpsertHighRiskJurisdictionDialog({
  row,
  open,
  onOpenChange,
  onSubmit,
}: {
  row: AdminHighRiskJurisdictionRowFragment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (input: {
    iso3CountryCode: string;
    source: HighRiskJurisdictionSource;
    reason: string | null;
  }) => Promise<void>;
}) {
  const { commonCountriesList, uncommonCountriesList } = getCountryLists();

  const form = useAppForm({
    defaultValues: {
      iso3CountryCode: row?.iso3CountryCode,
      source: row?.source,
      reason: row?.reason ?? "",
    } as Partial<FormSchema>,
    validators: {
      onSubmit: formSchema,
    },
    async onSubmit(formProps) {
      const formData = formSchema.parse(formProps.value);

      await onSubmit({
        iso3CountryCode: formData.iso3CountryCode,
        source: formData.source,
        reason: normalizeReasonInput(formData.reason),
      });
    },
  });

  useEffect(() => {
    if (!open) {
      form.reset();
    }
  }, [form, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {row ? "Edit high-risk jurisdiction" : "New high-risk jurisdiction"}
          </DialogTitle>
        </DialogHeader>
        <form.AppForm>
          <form
            className="flex flex-col gap-4"
            onSubmit={(event) => {
              event.preventDefault();
              event.stopPropagation();
              form.handleSubmit(event);
            }}
          >
            <form.AppField name="iso3CountryCode">
              {(field) => (
                <field.SelectField
                  label="Jurisdiction"
                  placeholder="Select a country"
                >
                  {commonCountriesList.map(({ value, label }) => (
                    <SelectItem key={`common-${value}`} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                  <SelectSeparator />
                  {uncommonCountriesList.map(({ value, label }) => (
                    <SelectItem key={`uncommon-${value}`} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="source">
              {(field) => (
                <field.SelectField label="Source" placeholder="Select a source">
                  {highRiskJurisdictionSourceValues.map((source) => (
                    <field.SelectItem key={source} value={source}>
                      {getSourceLabel(source)}
                    </field.SelectItem>
                  ))}
                </field.SelectField>
              )}
            </form.AppField>

            <form.AppField name="reason">
              {(field) => (
                <field.TextareaField
                  label="Reason"
                  placeholder="Optional reason"
                />
              )}
            </form.AppField>

            <form.SubmitButton>
              {row ? "Save changes" : "Create jurisdiction"}
            </form.SubmitButton>
          </form>
        </form.AppForm>
      </DialogContent>
    </Dialog>
  );
}

function normalizeReasonInput(reason?: string | null): string | null {
  if (reason === undefined || reason === null) {
    return null;
  }

  const trimmedReason = reason.trim();
  return trimmedReason.length > 0 ? trimmedReason : null;
}

function getCountryLabel(iso3CountryCode: string): string {
  return countries.getName(iso3CountryCode, "en") ?? iso3CountryCode;
}

function getSourceLabel(source: HighRiskJurisdictionSource): string {
  switch (source) {
    case "AUSTRAC":
      return "AUSTRAC";
    case "FATF":
      return "FATF";
    case "INTERNAL":
      return "Internal";
    default:
      return neverFunc(source);
  }
}
